"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAddressFormFields = exports.isPostcodeRequiredCountry = exports.extendedAddressFormKey = exports.extendedAddressFormFields = exports.extendedAddressSchema = exports.addressFormKey = exports.userAddressFormFields = exports.registerMemberAddressSchema = exports.addressFormFields = exports.userAddressSchema = exports.addressSchema = void 0;
const zod_1 = require("zod");
const error_messages_1 = require("@Entities/schemas/work/error-messages");
const form_field_types_1 = require("@App/enum/form-field-types");
const populate_schema_1 = __importDefault(require("@App/forms/v2/helpers/populate-schema"));
const error_messages_2 = require("@Const/error-messages");
const utils_1 = require("@App/common/utils");
Object.defineProperty(exports, "isPostcodeRequiredCountry", { enumerable: true, get: function () { return utils_1.isPostcodeRequiredCountry; } });
const validate_1 = require("@App/helpers/validate");
// Address schema property keys
const streetAddressKey = 'streetAddress';
const unitNumberKey = 'unitNumber';
const attentionKey = 'attention';
const cityKey = 'city';
const stateKey = 'state';
const postalCodeKey = 'postalCode';
const countryKey = 'countryId';
const address2Key = 'address2';
const address3Key = 'address3';
const address4Key = 'address4';
const streetAddressLabel = 'Street Address';
const unitNumberLabel = 'Unit Number';
const attentionLabel = 'Attention';
const cityLabel = 'City / Town';
const stateLabel = 'State / Province';
const postalCodeLabel = 'ZIP / Postal Code';
const countryLabel = 'Country';
const address2Label = 'Street Address - Line 1';
const address3Label = 'Street Address - Line 2';
const address4Label = 'Street Address - Line 3';
const addressFormKey = 'address';
exports.addressFormKey = addressFormKey;
const extendedAddressFormKey = 'extendedAddressForm';
exports.extendedAddressFormKey = extendedAddressFormKey;
const refineCountry = (address, ctx) => {
    if ((0, utils_1.isCountryUS)(address.countryId) || (0, utils_1.isCountryCAN)(address.countryId)) {
        if (!address.state) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: error_messages_2.REQUIRED_MESSAGE,
                path: [stateKey],
            });
        }
    }
    if ((0, utils_1.isPostcodeRequiredCountry)(address.countryId)) {
        if (!address.postalCode) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: error_messages_2.REQUIRED_MESSAGE,
                path: [postalCodeKey],
            });
        }
    }
    if ((0, utils_1.isCountryUS)(address.countryId)) {
        if (!(0, validate_1.validateUsPostalCode)(address.postalCode)) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: error_messages_2.IS_US_POSTAL_CODE_MESSAGE,
                path: [postalCodeKey],
            });
        }
    }
};
// The base address type used by the user sign up for
const baseAddressSchema = zod_1.z.object({
    [streetAddressKey]: zod_1.z
        .string({
        required_error: error_messages_2.REQUIRED_MESSAGE,
        invalid_type_error: error_messages_2.REQUIRED_MESSAGE,
    })
        .max(60, (0, error_messages_1.stringSizeMaxMessage)(streetAddressLabel, 60))
        .min(1, error_messages_2.REQUIRED_MESSAGE),
    [unitNumberKey]: zod_1.z.string().max(10, (0, error_messages_1.stringSizeMaxMessage)(unitNumberLabel, 10)).optional(),
    [attentionKey]: zod_1.z.string().max(240).optional(),
    [cityKey]: zod_1.z
        .string({
        required_error: error_messages_2.REQUIRED_MESSAGE,
    })
        .min(1, error_messages_2.REQUIRED_MESSAGE)
        .max(60, (0, error_messages_1.stringSizeMaxMessage)(cityLabel, 60)),
    [countryKey]: zod_1.z
        .string({
        required_error: error_messages_2.REQUIRED_MESSAGE,
        invalid_type_error: error_messages_2.REQUIRED_MESSAGE,
    })
        .min(1, error_messages_2.REQUIRED_MESSAGE),
    [stateKey]: zod_1.z.string().max(60, (0, error_messages_1.stringSizeMaxMessage)(stateLabel, 60)).optional(),
    [postalCodeKey]: zod_1.z.string().max(60, (0, error_messages_1.stringSizeMaxMessage)(postalCodeLabel, 60)).optional(),
});
// The schema used for the rightsholder forms
const addressSchema = baseAddressSchema.superRefine(refineCountry);
exports.addressSchema = addressSchema;
// The schema on the user sign-up form, does not need the attention key for this form.
const userAddressSchema = baseAddressSchema.omit({ [attentionKey]: true }).superRefine(refineCountry);
exports.userAddressSchema = userAddressSchema;
// The schema used for the public database contact form on the rightsholder page, we need fields to be optional now and to remove the .min() rules
const registerMemberAddressSchema = zod_1.z
    .object({
    [streetAddressKey]: zod_1.z
        .string({
        required_error: error_messages_2.REQUIRED_MESSAGE,
        invalid_type_error: error_messages_2.REQUIRED_MESSAGE,
    })
        .max(60, (0, error_messages_1.stringSizeMaxMessage)(streetAddressLabel, 60))
        .optional(),
    [unitNumberKey]: zod_1.z.string().max(10, (0, error_messages_1.stringSizeMaxMessage)(unitNumberLabel, 10)).optional(),
    [cityKey]: zod_1.z
        .string({
        required_error: error_messages_2.REQUIRED_MESSAGE,
    })
        .max(60, (0, error_messages_1.stringSizeMaxMessage)(cityLabel, 60))
        .optional(),
    [countryKey]: zod_1.z
        .string({
        required_error: error_messages_2.REQUIRED_MESSAGE,
        invalid_type_error: error_messages_2.REQUIRED_MESSAGE,
    })
        .optional(),
    [stateKey]: zod_1.z.string().max(60, (0, error_messages_1.stringSizeMaxMessage)(stateLabel, 60)).optional(),
    [postalCodeKey]: zod_1.z.string().max(60, (0, error_messages_1.stringSizeMaxMessage)(postalCodeLabel, 60)).optional(),
})
    .superRefine((address, ctx) => {
    const anyAddressFieldHasValueWhileOthersDont = (address.streetAddress !== undefined &&
        address.streetAddress.length > 0 &&
        (!address.city || !address.countryId)) ||
        (address.city !== undefined && address.city.length > 0 && (!address.streetAddress || !address.countryId)) ||
        (address.countryId !== undefined &&
            address.countryId.length > 0 &&
            (!address.streetAddress || !address.city));
    if (anyAddressFieldHasValueWhileOthersDont) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'Required',
            path: [streetAddressKey],
        });
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'Required',
            path: [cityKey],
        });
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'Required',
            path: [countryKey],
        });
    }
    refineCountry(address, ctx);
});
exports.registerMemberAddressSchema = registerMemberAddressSchema;
const extendedAddressSchema = zod_1.z
    .object({
    [cityKey]: zod_1.z
        .string({
        required_error: error_messages_2.REQUIRED_MESSAGE,
    })
        .min(1, error_messages_2.REQUIRED_MESSAGE)
        .max(60, (0, error_messages_1.stringSizeMaxMessage)(cityLabel, 60)),
    [address2Key]: zod_1.z
        .string({
        required_error: error_messages_2.REQUIRED_MESSAGE,
    })
        .min(1, error_messages_2.REQUIRED_MESSAGE)
        .max(240, (0, error_messages_1.stringSizeMaxMessage)(address2Label, 240)),
    [address3Key]: zod_1.z.string().max(240, (0, error_messages_1.stringSizeMaxMessage)(address3Label, 240)).optional().nullish(),
    [address4Key]: zod_1.z.string().max(240, (0, error_messages_1.stringSizeMaxMessage)(address4Label, 240)).optional().nullish(),
    [countryKey]: zod_1.z
        .string({
        required_error: error_messages_2.REQUIRED_MESSAGE,
        invalid_type_error: error_messages_2.REQUIRED_MESSAGE,
    })
        .min(1, error_messages_2.REQUIRED_MESSAGE),
    [stateKey]: zod_1.z.string().max(60, (0, error_messages_1.stringSizeMaxMessage)(stateLabel, 60)).optional(),
    [postalCodeKey]: zod_1.z.string().max(60, (0, error_messages_1.stringSizeMaxMessage)(postalCodeLabel, 60)).optional(),
})
    .superRefine(refineCountry);
exports.extendedAddressSchema = extendedAddressSchema;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fullAddressSchema = zod_1.z.union([addressSchema, registerMemberAddressSchema, userAddressSchema]);
const extendedAddressFormFieldsNoSchema = {
    [cityKey]: {
        name: cityKey,
        label: cityLabel,
        parentObjectName: extendedAddressFormKey,
        type: form_field_types_1.FormFieldTypes.TEXT,
    },
    [countryKey]: {
        name: countryKey,
        parentObjectName: extendedAddressFormKey,
        label: countryLabel,
        type: form_field_types_1.FormFieldTypes.DROPDOWN,
        options: [],
    },
    [stateKey]: {
        name: stateKey,
        label: stateLabel,
        parentObjectName: extendedAddressFormKey,
        type: form_field_types_1.FormFieldTypes.TEXT,
    },
    [postalCodeKey]: {
        name: postalCodeKey,
        parentObjectName: extendedAddressFormKey,
        label: postalCodeLabel,
        type: form_field_types_1.FormFieldTypes.TEXT,
    },
    [address2Key]: {
        name: address2Key,
        parentObjectName: extendedAddressFormKey,
        label: address2Label,
        type: form_field_types_1.FormFieldTypes.TEXT,
    },
    [address3Key]: {
        name: address3Key,
        parentObjectName: extendedAddressFormKey,
        label: address3Label,
        type: form_field_types_1.FormFieldTypes.TEXT,
    },
    [address4Key]: {
        name: address4Key,
        parentObjectName: extendedAddressFormKey,
        label: address4Label,
        type: form_field_types_1.FormFieldTypes.TEXT,
    },
};
// Defines how the user address form fields will render, we don't need the attention field.
const createUserAddressFormFields = (parentObjectName, zodObject) => {
    const addressFormFieldsNoSchema = {
        [streetAddressKey]: {
            name: streetAddressKey,
            parentObjectName: parentObjectName,
            label: streetAddressLabel,
            type: form_field_types_1.FormFieldTypes.TEXT,
        },
        [unitNumberKey]: {
            name: unitNumberKey,
            parentObjectName: parentObjectName,
            label: unitNumberLabel,
            type: form_field_types_1.FormFieldTypes.TEXT,
        },
        [cityKey]: {
            name: cityKey,
            label: cityLabel,
            parentObjectName: parentObjectName,
            type: form_field_types_1.FormFieldTypes.TEXT,
        },
        [countryKey]: {
            name: countryKey,
            parentObjectName: parentObjectName,
            label: countryLabel,
            type: form_field_types_1.FormFieldTypes.DROPDOWN,
            options: [],
        },
        [stateKey]: {
            name: stateKey,
            label: stateLabel,
            parentObjectName: parentObjectName,
            type: form_field_types_1.FormFieldTypes.TEXT,
        },
        [postalCodeKey]: {
            name: postalCodeKey,
            parentObjectName: parentObjectName,
            label: postalCodeLabel,
            type: form_field_types_1.FormFieldTypes.TEXT,
        },
    };
    return (0, populate_schema_1.default)(addressFormFieldsNoSchema, zodObject);
};
// Defines how the rightsholder form fields will render
const createAddressFormFields = (parentObjectName, zodObject) => {
    const addressFormFieldsNoSchema = {
        [streetAddressKey]: {
            name: streetAddressKey,
            parentObjectName: parentObjectName,
            label: streetAddressLabel,
            type: form_field_types_1.FormFieldTypes.TEXT,
        },
        [unitNumberKey]: {
            name: unitNumberKey,
            parentObjectName: parentObjectName,
            label: unitNumberLabel,
            type: form_field_types_1.FormFieldTypes.TEXT,
        },
        [attentionKey]: {
            name: attentionKey,
            parentObjectName: parentObjectName,
            label: attentionLabel,
            type: form_field_types_1.FormFieldTypes.TEXT,
        },
        [cityKey]: {
            name: cityKey,
            label: cityLabel,
            parentObjectName: parentObjectName,
            type: form_field_types_1.FormFieldTypes.TEXT,
        },
        [countryKey]: {
            name: countryKey,
            parentObjectName: parentObjectName,
            label: countryLabel,
            type: form_field_types_1.FormFieldTypes.DROPDOWN,
            options: [],
        },
        [stateKey]: {
            name: stateKey,
            label: stateLabel,
            parentObjectName: parentObjectName,
            type: form_field_types_1.FormFieldTypes.TEXT,
        },
        [postalCodeKey]: {
            name: postalCodeKey,
            parentObjectName: parentObjectName,
            label: postalCodeLabel,
            type: form_field_types_1.FormFieldTypes.TEXT,
        },
    };
    return (0, populate_schema_1.default)(addressFormFieldsNoSchema, zodObject);
};
exports.createAddressFormFields = createAddressFormFields;
// creates the form fields by using the schemas
const userAddressFormFieldsNoSchema = createUserAddressFormFields(addressFormKey, userAddressSchema);
const userAddressFormFields = (0, populate_schema_1.default)(userAddressFormFieldsNoSchema, userAddressSchema);
exports.userAddressFormFields = userAddressFormFields;
const addressFormFieldsNoSchema = createAddressFormFields(addressFormKey, addressSchema);
const addressFormFields = (0, populate_schema_1.default)(addressFormFieldsNoSchema, addressSchema);
exports.addressFormFields = addressFormFields;
const extendedAddressFormFields = (0, populate_schema_1.default)(extendedAddressFormFieldsNoSchema, extendedAddressSchema);
exports.extendedAddressFormFields = extendedAddressFormFields;
